import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { cookie_domain } from 'src/environments/environment';

export type SiteVisitCookieState = {
  uuid: string | null;
  timestamp: string | null;
};

@Injectable({ providedIn: 'root' })
export class SiteVisitCookieService {
  private readonly uuidCookieName = 'cuentame_site_visit_uuid';
  private readonly timestampCookieName = 'cuentame_site_visit_timestamp';

  private readonly ttlMinutes = 30;

  public getSiteVisit(): SiteVisitCookieState {
    return {
      uuid: this.getCookie(this.uuidCookieName),
      timestamp: this.getCookie(this.timestampCookieName),
    };
  }

  public setSiteVisit(uuid: string, timestamp: string): void {
    const expires = DateTime.now()
      .plus({ minutes: this.ttlMinutes })
      .toUTC()
      .toJSDate()
      .toUTCString();

    this.setCookie(this.uuidCookieName, uuid, expires);
    this.setCookie(this.timestampCookieName, timestamp, expires);
  }

  public refreshTimestamp(timestamp: string): void {
    const { uuid } = this.getSiteVisit();

    if (!uuid) return;

    this.setSiteVisit(uuid, timestamp);
  }

  public isValid(): boolean {
    const { uuid, timestamp } = this.getSiteVisit();

    if (!uuid || !timestamp) return false;

    const parsedTimestamp = DateTime.fromISO(timestamp, { setZone: true });

    if (!parsedTimestamp.isValid) return false;

    const diffMinutes = DateTime.now()
      .toUTC()
      .diff(parsedTimestamp.toUTC(), 'minutes')
      .minutes;

    return diffMinutes <= this.ttlMinutes;
  }

  public clear(): void {
    this.deleteCookie(this.uuidCookieName);
    this.deleteCookie(this.timestampCookieName);
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );

    return match ? decodeURIComponent(match[2]) : null;
  }

  private setCookie(name: string, value: string, expires: string): void {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    const domainPart =
      cookie_domain && !isLocalhost
        ? ` domain=${cookie_domain};`
        : '';

    const securePart = isLocalhost ? '' : ' secure;';
    const sameSitePart = isLocalhost ? ' SameSite=Lax;' : ' SameSite=None;';

    document.cookie =
      `${name}=${encodeURIComponent(value)};` +
      ` path=/;` +
      domainPart +
      securePart +
      sameSitePart +
      ` expires=${expires}`;
  }

  private deleteCookie(name: string): void {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    const domainPart =
      cookie_domain && !isLocalhost
        ? ` domain=${cookie_domain};`
        : '';

    const securePart = isLocalhost ? '' : ' secure;';
    const sameSitePart = isLocalhost ? ' SameSite=Lax;' : ' SameSite=None;';

    document.cookie =
      `${name}=;` +
      ` path=/;` +
      domainPart +
      securePart +
      sameSitePart +
      ` expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}